import React, { useEffect, useState } from 'react';
import './App.css';
import { LicenseInfo } from '@mui/x-license';
import { useAuth } from 'react-oidc-context';
import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CreateApi from './Settings/appSettings';
import { useLSContext } from './Context/useLSContext';
import { setAppTheme } from './Settings/appTheme';
import { isUpdateAvailable } from './utils/serviceWorkerRegistration';
import { useCache } from './Context/CacheContext';
import { LiveScoringLogin } from './Components/LiveScoringLogIn';
import { ScoringContent } from './Components/ScoringContent';
import { createCookie, getCookie, deleteCookie } from "./Context/CookieContext";
import { useDataContext } from './Context/useDataContext';
import { useHoleSettings } from './Context/useHoleSettingsContext';
import { Leaderboard } from './Components/Leaderboard';
import { EventSelectionWindow } from './Components/EventSelectionWindow';
import { LSHeader } from './Components/LSHeader';
import { FloatingActionBar } from './Components/FloatingActionBar';
import { useTokenManager } from '@moment-golf/auth';
import { FirstTeeDrawer } from './Components/FirstTeeDrawer';
import { resolvedStartHole } from '@moment-golf/shared';
import { PendingScoreProvider } from './Context/PendingScoreContext';
import { MessageProvider } from './Context/useMessageContext';
import { getLastHole, saveLastHole, clearLastHole } from './utils/lastHoleStorage';

interface FirstTeeContext {
  eventData: any;
  courseData: any;
  registrationData: any[];
  roundNumber: number;
  teeTime: string | Date;
  startingHole: number;
  scoreCode: string;
  eventID: string;
}

interface AppContentProps {
  fetchScoringData: (scoreCodeParam: string, checkCookies: boolean, eventIdParam?: string) =>
    Promise<{ success: boolean; requiresGroupSelection?: boolean; eventID: string; scoreCode: string }>;
  firstTeeDrawerOpen: boolean;
  setFirstTeeDrawerOpen: (v: boolean) => void;
  firstTeeContext: FirstTeeContext | null;
}

function AppContent({ fetchScoringData, firstTeeDrawerOpen, setFirstTeeDrawerOpen, firstTeeContext }: AppContentProps) {
  const navigate = useNavigate();
  const { adminMode, scoreCode } = useDataContext();

  return (
    <>
      {!adminMode && <FloatingActionBar preHoleRefresh={() => {
        if (window.location.pathname.includes('/live-scoring')) {
          fetchScoringData(scoreCode, true);
          return Promise.resolve();
        }
        return Promise.resolve();
      }} />}
      <Routes>
        <Route path="/" element={<LiveScoringLogin fetchScoringData={fetchScoringData} />} />
        <Route path="/login" element={<LiveScoringLogin fetchScoringData={fetchScoringData} />} />
        <Route path="/live-scoring" element={<ScoringContent fetchScoringData={fetchScoringData} />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
      </Routes>
      {firstTeeContext && (
        <FirstTeeDrawer
          open={firstTeeDrawerOpen}
          onClose={() => setFirstTeeDrawerOpen(false)}
          eventData={firstTeeContext.eventData}
          courseData={firstTeeContext.courseData}
          registrationData={firstTeeContext.registrationData}
          currentRound={firstTeeContext.roundNumber}
          currentTeeTime={firstTeeContext.teeTime}
          startingHole={firstTeeContext.startingHole}
          scoreCode={firstTeeContext.scoreCode}
          onComplete={async () => {
            setFirstTeeDrawerOpen(false);
            const result = await fetchScoringData(firstTeeContext.scoreCode, false);
            if (result.success) {
              navigate(`/live-scoring/?event=${firstTeeContext.eventID}&scoreCode=${firstTeeContext.scoreCode}`);
            }
          }}
        />
      )}
    </>
  );
}

if (process.env.REACT_APP_MUI_X_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENSE_KEY);
}

function App() {

  const auth = useAuth();
  const api = CreateApi();
  const { factor, appTheme, setFactor, setTheme,  windowSize, setCurrentHole, setScoreLock } = useLSContext();
  const { adminMode, setAdminMode, setScoreCode, setEventData, setRegistrationData, setCourseData, scoreCode,
     setScoreData, setOwnEventScore, setCurrentRound, setStartHole, setLeaderboardData, setLeaderboardLastQuery, setLeaderboardCourseData, eventData} = useDataContext();
  const { setHoleSettings } = useHoleSettings();
  const [updateAvailable, setUpdateAvailable] = useState(isUpdateAvailable());
  const [firstTeeDrawerOpen, setFirstTeeDrawerOpen] = useState(false);
  const [firstTeeContext, setFirstTeeContext] = useState<FirstTeeContext | null>(null);

  const resetAppState = () => {
    setEventData({});
    setCourseData({});
    setScoreData([]);
    setOwnEventScore(null);
    setRegistrationData([]);
    setLeaderboardData([]);
    setLeaderboardCourseData([]);
    setLeaderboardLastQuery(new Date('1/1/2000'));
    setScoreCode('');
    setCurrentRound(0);
    setStartHole(0);
    setCurrentHole(1);
    setScoreLock(false);
    clearLastHole();
    setFirstTeeContext(null);
    setFirstTeeDrawerOpen(false);
  };
  
 /*  useEffect(() => {
    if (adminMode) {
      setFactor(2)
    } else {
      setFactor(1)
    }
  }, [adminMode]); */

  useEffect(() => {
    setTheme(setAppTheme(factor));
  }, [factor]);

  useTokenManager(auth);


  const findActiveRound = (eventScore: any) => {
    const roundScores = eventScore.roundScores;
    let activeRound = null;
    
    // Go through each round in roundScores to find first unsubmitted and unconfirmed round
    for (const roundID in roundScores) {
      const round = roundScores[roundID];
      if (round.submitted === false && round.confirmed === false) {
        activeRound = round;
        break; // Found the active round, exit the loop
      }        
    }

    if (!activeRound) {
      const roundLength = roundScores.length;
      activeRound = roundScores[roundLength-1];
      //setScoreLock(true);
    }

    // console.log('Active Round:', activeRound);
    
    return activeRound;
  }

  const newHoleSettings = (newHole: number, eventData: any, courseData: any) => {
     
    if (eventData && Object.keys(eventData).length > 0 && 
        courseData && Object.keys(courseData).length > 0 &&
        newHole > 0) {

        const playerTeeSet = eventData.playerTeeSet || 
            (courseData.teeSets && courseData.teeSets.length > 0 ? courseData.teeSets[0].teeName : null);

        if (playerTeeSet && courseData.teeSets) {
            const teeSet = courseData.teeSets.find((tee: any) => tee.teeName === playerTeeSet);

            if (teeSet && teeSet.holes && teeSet.holes.length >= newHole) {
                const teesData = teeSet.holes[newHole-1];

                setHoleSettings( {
                        hole: newHole,
                        par: teesData.par,
                        dist: teesData.dist,
                        hdcp: teesData.hdcp
                });
            }
        }
    }
   }

  const fetchScoringData = async (scoreCodeParam: string, checkCookies: boolean, eventIdParam?: string): Promise<{ success: boolean; requiresGroupSelection?: boolean; eventID: string; scoreCode: string }> => {

    const scoreCode = scoreCodeParam || (checkCookies ? getCookie('momentGolf-ls-current-scoreCode') : '');
    const eventId   = eventIdParam   || (checkCookies ? getCookie('momentGolf-ls-current-eventId')  : undefined);
  
    if (!scoreCode) {
      console.error('No score code provided or found in cookies');
      return {
        success: false,
        eventID: '',
        scoreCode: ''
      };
    }

    const cleanCode = scoreCode.replace('-admin', '');
    if (cleanCode !== scoreCode) {
      setAdminMode(true);
    }
    
    // Check score code validity
    const scoreCodeResponse = await api.get('/ls/CheckScoreCode', {
      params: {
        scoreCode: cleanCode.toUpperCase(),
        ...(eventId ? { eventId } : {}),
      }
    });

    // console.log('Check Score Code Response:', scoreCodeResponse.data);
    const result = scoreCodeResponse.data;
    const codeStatus: boolean = result?.length > 0;

    if (!codeStatus) {
      alert('The provided score code is either invalid or is not available yet. Please check the score code and try again.');
      setScoreCode('');
      deleteCookie('momentGolf-ls-current-scoreCode');
      deleteCookie('momentGolf-ls-current-eventId');
      return {
        success: false,
        eventID: '',
        scoreCode: ''
      };
    }

    // groupSelfSelection flow: no EventScore exists yet — open FirstTeeDrawer
    if (result[0]?.requiresGroupSelection) {
      const {
        eventID,
        scoreCode: regScoreCode,
        roundNumber = 1,
        teeTime = new Date().toISOString(),
        startingHole: apiStartingHole,
        shotgunOrder: apiShotgunOrder,
      } = result[0];
      setScoreCode(regScoreCode);
      createCookie('momentGolf-ls-current-scoreCode', regScoreCode, 0.5);
      createCookie('momentGolf-ls-current-eventId', eventID, 0.5);
      try {
        const firstTeeLsParams: Record<string, unknown> = {
          eventID,
          roundNumber,
          teeTime,
          scoreCode: regScoreCode,
        };
        const parsedStartingHole = Number(apiStartingHole);
        if (Number.isFinite(parsedStartingHole) && parsedStartingHole > 0) {
          firstTeeLsParams.startingHole = Math.round(parsedStartingHole);
        }
        if (apiShotgunOrder != null && apiShotgunOrder !== '') {
          firstTeeLsParams.shotgunOrder = apiShotgunOrder;
        }
        const lsData = await api.get('/ls/GetLiveScoringData', {
          params: firstTeeLsParams,
        });
        const eventInfo = lsData.data.eventInfo;
        const roundCfg =
          Array.isArray(eventInfo?.rounds)
            ? eventInfo.rounds.find((r: any) => Number(r?.roundNumber) === Number(roundNumber))
            : null;
        const startingHole =
          apiStartingHole != null && Number(apiStartingHole) > 0 && Number.isFinite(Number(apiStartingHole))
            ? Math.round(Number(apiStartingHole))
            : resolvedStartHole(roundCfg);
        setFirstTeeContext({
          eventData: eventInfo,
          courseData: lsData.data.courseInfo,
          registrationData: [],
          roundNumber,
          teeTime,
          startingHole,
          scoreCode: regScoreCode,
          eventID,
        });
      } catch (err) {
        console.error('[fetchScoringData] failed to load event data for first-tee flow', err);
      }
      setFirstTeeDrawerOpen(true);
      return { success: false, requiresGroupSelection: true, eventID, scoreCode: regScoreCode };
    }

    if (result.length > 1) {
      console.warn('Multiple events found for this score code. Defaulting to the first.');
    }

    // Set score code and create cookies
    setScoreCode(scoreCode);
    createCookie('momentGolf-ls-current-scoreCode', scoreCode, 0.5); // 12 hr expiry
    createCookie('momentGolf-ls-current-eventId', result[0].eventID, 0.5); // 12 hr expiry
    createCookie('momentGolf-ls-myEventScoreIDs', result[0]._id, 0.5); // 12 hr expiry

    const activeRound = findActiveRound(result[0]);

    const liveScoringParams: Record<string, unknown> = {
      eventID: result[0].eventID,
      roundNumber: activeRound.roundNumber,
      teeTime: new Date(activeRound.teeTime).toISOString(),
      scoreCode: cleanCode,
      adminMode: adminMode,
    };
    const activeStartingHole = Number(activeRound.startingHole);
    if (Number.isFinite(activeStartingHole) && activeStartingHole > 0) {
      liveScoringParams.startingHole = Math.round(activeStartingHole);
    }
    if (activeRound.shotgunOrder != null && activeRound.shotgunOrder !== '') {
      liveScoringParams.shotgunOrder = activeRound.shotgunOrder;
    }

    const liveScoringData = await api.get('/ls/GetLiveScoringData', {
      params: liveScoringParams,
    });

    // console.log('Live Scoring Data:', liveScoringData.data);

    const markerEventScores = liveScoringData.data.markerEventScores;

    setEventData(liveScoringData.data.eventInfo);
    setRegistrationData(liveScoringData.data.playerRegistrations);
    setCourseData(liveScoringData.data.courseInfo);
    setScoreData(markerEventScores);
    setOwnEventScore(liveScoringData.data.playerEventScore ?? null);

    const locked = markerEventScores[0].roundScores[activeRound.roundNumber-1]?.confirmed &&
      markerEventScores[0].roundScores[activeRound.roundNumber-1]?.submitted;

    setScoreLock(locked);

    if (activeRound) {

      // console.log('Marker Event Scores:', markerEventScores);

      const activeRoundScore = markerEventScores[0]?.roundScores?.[activeRound.roundNumber - 1];
      /** Round-score field is `startingHole` (where the group tees off); support legacy `startHole` if present */
      const roundStartingHole = Number(
        activeRoundScore?.startingHole ?? (activeRoundScore as { startHole?: number })?.startHole,
      );
      const startHole =
        Number.isFinite(roundStartingHole) && roundStartingHole > 0 ? Math.round(roundStartingHole) : 1;

      const reorderedHoleScores: any[] = [];
      const holeScores = [...(activeRoundScore?.holeScores ?? [])];

      if (startHole === 1) {
          reorderedHoleScores.push(...holeScores);
      } else {
        const startForward = holeScores.splice(startHole - 1);
        const startBackward = holeScores.splice(0, startHole - 1);
        reorderedHoleScores.push(...startForward, ...startBackward);
      }
      // console.log('Reordered Hole Scores:', reorderedHoleScores);

      const firstIncompleteEntry = reorderedHoleScores.find((score: any) => 
        liveScoringData.data.eventInfo.scoreType.toLowerCase() === 'opponent' 
          ?
          score.playerScore === 0 || score.playerScore === null || 
          score.markerScore === 0 || score.markerScore === null 
          :
          score.playerScore === 0 || score.playerScore === null
      );

      const firstIncompleteHole = firstIncompleteEntry?.holeNumber ?? 1;
      const roundTotalHoles = reorderedHoleScores.length
        || Number(liveScoringData.data.eventInfo?.totalHoles)
        || 18;
      const savedHole = getLastHole(cleanCode, activeRound.roundNumber);
      const holeNumber =
        savedHole != null && savedHole >= 1 && savedHole <= roundTotalHoles
          ? savedHole
          : firstIncompleteHole;

      newHoleSettings(holeNumber, liveScoringData.data.eventInfo, liveScoringData.data.courseInfo);
      setCurrentHole(holeNumber);
      saveLastHole(cleanCode, activeRound.roundNumber, holeNumber);
      setCurrentRound(activeRound.roundNumber);
      setStartHole(startHole);
    } else {
      setScoreLock(true);
    }

    try {
      localStorage.setItem('momentGolf-ls-splashCache', JSON.stringify({
        eventName: liveScoringData.data.eventInfo?.eventName ?? '',
        courseName: liveScoringData.data.courseInfo?.courseName ?? '',
        round: activeRound.roundNumber,
        cachedAt: new Date().toISOString(),
      }));
    } catch {
      // localStorage write failure is non-fatal
    }

    return {
      success: true,
      eventID: result[0].eventID,
      scoreCode: scoreCode
    };

  }


  return (
    <div className="App live-scoring" style={{ 
      height: '100%',
      width: '100%',
      minHeight: '100dvh',
      minWidth: '100dvw',
      overflow: 'hidden',
      position: 'absolute',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingTop: 'env(safe-area-inset-top)',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'none',
      backgroundColor: 'sepia.background',
      //border: '1px solid red'
    }}>
      <ThemeProvider theme={appTheme}>
        <Router>
          <MessageProvider>
            <PendingScoreProvider>
              <LSHeader onRestart={resetAppState} />
              <AppContent
                fetchScoringData={fetchScoringData}
                firstTeeDrawerOpen={firstTeeDrawerOpen}
                setFirstTeeDrawerOpen={setFirstTeeDrawerOpen}
                firstTeeContext={firstTeeContext}
              />
            </PendingScoreProvider>
          </MessageProvider>
        </Router>
      </ThemeProvider>
    </div>
  );
}

export default App;
